import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { startPlanCheckoutOrLogin } from '../services/billingservice';
import LoginRequiredNotice from '../components/LoginRequiredNotice.jsx';
import styles from '../style/AnomalyDetectionPage.module.css';

function AnomalyDetectionPage() {
    const { user, userProducts, plans } = useAuth();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // State for anomaly detection functionality
    const [loadedFiles, setLoadedFiles] = useState([]);
    const [activeFile, setActiveFile] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Compute anomaly access
    const hasAnomaly =
        !!plans?.anomaly?.active ||
        userProducts?.some(p => p.product_name === 'anomaly' && p.is_active);

    // If not logged in, show login notice
    if (!user) {
        return (
            <LoginRequiredNotice 
                title="Melden Sie sich an, um Anomalieerkennung zu nutzen"
                subtitle="Nach der Anmeldung können Sie den Anomalie-Plan kaufen und sofort starten."
            />
        );
    }

    const handlePurchasePlan = async (plan) => {
        try {
            await startPlanCheckoutOrLogin(plan, user, navigate);
        } catch (error) {
            console.error('Purchase failed:', error);
        }
    };

    // If logged in but no plan, show the professional upgrade page
    if (!hasAnomaly) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.decorativeBg} aria-hidden="true">
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 1440 600"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.bgSvg}
                    >
                        <defs>
                            <linearGradient id="bg-gradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#eaf1fb" />
                                <stop offset="100%" stopColor="#c6d4e8" />
                            </linearGradient>
                            <radialGradient id="blob1" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                                <stop offset="0%" stopColor="#1c3a7a" stopOpacity="0.12" />
                                <stop offset="100%" stopColor="#058744" stopOpacity="0.08" />
                            </radialGradient>
                        </defs>
                        <ellipse cx="300" cy="120" rx="320" ry="120" fill="url(#blob1)" />
                        <ellipse cx="1200" cy="500" rx="260" ry="100" fill="url(#blob1)" />
                    </svg>
                </div>


                <section className={styles.heroSection}>
                    <div className={styles.sectionWrapper}>
                        <div className={styles.heroGraphics}>
                            <div className={styles.heroCircle1}></div>
                            <div className={styles.heroCircle2}></div>
                            <div className={styles.heroDots}></div>
                        </div>
                        <h1 className={styles.heroHeadline}></h1>
                        <p className={styles.heroSubheadline}>
                            
                        </p>
                        <p className={styles.heroTrial}>
                            
                        </p>
                    </div>
                </section>
            

      
                <section className={styles.needPlanBanner}>
                    <div className={styles.sectionWrapper}>
                        <div className={styles.bannerContent}>
                            <div className={styles.bannerIcon}>🔍</div>
                            <div className={styles.bannerText}>
                                <h3>Sie benötigen einen aktiven Plan für die Anomalieerkennung</h3>
                                <p>Starten Sie Ihre kostenlose 30-tägige Testphase, um alle Funktionen freizuschalten und vollen Zugriff zu erhalten.</p>
                            </div>
                            <button
                                className={styles.bannerButton}
                                onClick={() => handlePurchasePlan('anomaly')}
                            >
                                Kostenlose Testphase starten
                            </button>
                        </div>
                    </div>
                </section>
    

              
                
   

           
                
             

    
                
           

                
                
     
            </div>
        );
    }

    const filteredFiles = loadedFiles.filter(file => 
        file.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        console.log('Files uploaded:', files);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Anomalieerkennung Dashboard</h1>
                <p>Analysieren Sie Ihre industriellen Daten auf Anomalien und Muster</p>
            </div>

            <div className={styles.controlSection}>
                <div className={styles.fileControls}>
                    <button 
                        className={styles.importButton} 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading}
                    >
                        Dateien importieren
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        multiple
                        accept=".csv,.xlsx,.xls,.zip"
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            <div className={styles.mainContent}>
                <div className={styles.sidebar}>
                    <div className={styles.fileList}>
                        <h3>Geladene Dateien ({loadedFiles.length})</h3>
                        <input
                            type="text"
                            placeholder="Dateien durchsuchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                        {filteredFiles.length > 0 ? (
                            filteredFiles.map((file, index) => (
                                <div
                                    key={index}
                                    className={`${styles.fileItem} ${activeFile === file ? styles.activeFile : ''}`}
                                    onClick={() => setActiveFile(file)}
                                >
                                    <span>{file.name}</span>
                                </div>
                            ))
                        ) : (
                            <div className={styles.emptyFileList}>
                                Keine Dateien geladen. Importieren Sie Dateien, um zu beginnen.
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.analysisArea}>
                    {activeFile ? (
                        <div className={styles.analysisControls}>
                            <h3>Analysiere: {activeFile.name}</h3>
                            <p>Die Analyse-Schnittstelle würde hier für die aktive Anomalieerkennung angezeigt werden.</p>
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>📊</div>
                            <h3>Bereit für die Analyse</h3>
                            <p>Laden Sie Dateien hoch oder wählen Sie eine Datei aus der Seitenleiste, um mit der Anomalieerkennung zu beginnen</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AnomalyDetectionPage;
